import { ETaskStatus } from '@constants';
import { IPaginatedResponse, ITask } from '@sdk/contracts';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { create } from 'zustand';

import { useProfile } from '../profile';

import { sdk } from '@/services';

const TaskService = sdk.getService('TaskService');

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 10;

interface IStore {
  selectedCourse?: string;
  selectedTeacher?: string;
  setSelectedCourse: (course?: string) => void;
  setSelectedTeacher: (teacher: string) => void;
}

const store = create<IStore>(set => ({
  setSelectedCourse: course => set({ selectedCourse: course }),
  setSelectedTeacher: teacher => set({ selectedTeacher: teacher })
}));

export const useCourseTasks = () => {
  const {
    profile,
    roleCheck: { isTeacher }
  } = useProfile();
  const { selectedCourse, selectedTeacher, setSelectedCourse, setSelectedTeacher } = store();
  const teacherId = isTeacher ? profile?._id : selectedTeacher;

  const getInfiniteQueryParams = (status: ETaskStatus) => {
    return {
      queryKey: ['courses', 'tasks', selectedCourse, teacherId, status],
      queryFn: ({ pageParam = INITIAL_PAGE }) =>
        TaskService.findTasks(
          { courseId: selectedCourse, assigneeId: teacherId, status },
          pageParam,
          INITIAL_LIMIT
        ),
      initialPageParam: INITIAL_PAGE,
      getNextPageParam: (lastPage: IPaginatedResponse<ITask>) => {
        if (!lastPage.hasNextPage) return undefined;
        return lastPage.page + 1;
      },
      enabled: !!selectedCourse || !!teacherId
    };
  };

  const {
    isLoading: isLoadingPendingAndRejected,
    data: dataPendingAndRejected,
    fetchNextPage: fetchNextPagePendingAndRejected,
    isFetchingNextPage: isFetchingNextPagePendingAndRejected
  } = useInfiniteQuery(getInfiniteQueryParams(ETaskStatus.PENDING));

  const {
    isLoading: isLoadingReview,
    data: dataReview,
    fetchNextPage: fetchNextPageReview,
    isFetchingNextPage: isFetchingNextPageReview
  } = useInfiniteQuery(getInfiniteQueryParams(ETaskStatus.IN_REVIEW));

  const {
    isLoading: isLoadingApproved,
    data: dataApproved,
    fetchNextPage: fetchNextPageApproved,
    isFetchingNextPage: isFetchingNextPageApproved
  } = useInfiniteQuery(getInfiniteQueryParams(ETaskStatus.APPROVED));

  const { data: dataGantt, isLoading: isLoadingGantt } = useQuery({
    queryKey: ['courses', 'tasks', 'gantt', selectedCourse, selectedTeacher],
    queryFn: () => TaskService.getCourseGanttChartData(selectedCourse!, selectedTeacher),
    enabled: !!selectedCourse
  });

  const isLoading = isLoadingPendingAndRejected || isLoadingReview || isLoadingApproved;

  const isEmptyPendingAndRejected = useMemo(() => {
    return dataPendingAndRejected?.pages[dataPendingAndRejected.pages.length - 1]?.totalDocs === 0;
  }, [dataPendingAndRejected]);

  const isEmptyReview = useMemo(() => {
    return dataReview?.pages[dataReview.pages.length - 1]?.totalDocs === 0;
  }, [dataReview]);

  const isEmptyApproved = useMemo(() => {
    return dataApproved?.pages[dataApproved.pages.length - 1]?.totalDocs === 0;
  }, [dataApproved]);

  return {
    isLoading,
    pendingAndRejectedTasks: dataPendingAndRejected?.pages.flatMap(page => page.docs),
    reviewTasks: dataReview?.pages.flatMap(page => page.docs),
    approvedTasks: dataApproved?.pages.flatMap(page => page.docs),
    isEmptyPendingAndRejected,
    isEmptyReview,
    isEmptyApproved,
    isAllEmpty: isEmptyPendingAndRejected && isEmptyReview && isEmptyApproved,
    selectedCourse,
    selectedTeacher,
    setSelectedCourse,
    setSelectedTeacher,
    fetchNextPagePendingAndRejected,
    fetchNextPageReview,
    fetchNextPageApproved,
    isFetchingNextPagePendingAndRejected,
    isFetchingNextPageReview,
    isFetchingNextPageApproved,
    dataGantt,
    isLoadingGantt
  };
};
